import { ColumnDef } from '@tanstack/react-table';
import { Loader, Stack, WaterIcon } from 'component-library';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary } from '@/components';
import { WaterExplainer } from '@/components/NCExplainers/WaterExplainer';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useWidgetSlotConfigurationByNameType } from '@/hooks/useWidgetConfigurationByOrganizationId';

import { LandMonitoringDetailPageWrapper } from '../../components/LandMonitoring.page-wrapper';
import { FactCell, FactTrendCell, PlotRow } from '../../components/plot-list/Cells';
import { PlotListDesktop } from '../../components/plot-list/PlotListDesktop';
import { PlotListTitle } from '../../components/plot-list/PlotListTitle';
import { ToggleSortButton } from '../../components/plot-list/ToggleSortButton';

export const WaterDetailPage = () => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';

  const customPlotListColumns: ColumnDef<PlotRow>[] = [
    {
      id: 'waterHoldingCapacity',
      accessorKey: 'waterHoldingCapacity',
      header: ({ column }) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.water.labels.waterHoldingCapacity')}
        </ToggleSortButton>
      ),
      cell: ({ row }) => {
        return (
          <FactCell
            plot={row.original}
            factType={R1FactType.r1_water_holding_capacity_per_ha}
            unitType={UnitEnum['m^3/ha']}
            icon={WaterIcon}
          />
        );
      },
    },
    {
      id: 'soilMoisture',
      accessorKey: 'soilMoisture',
      header: ({ column }) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.water.labels.soilMoisture')}
        </ToggleSortButton>
      ),
      cell: ({ row }) => {
        return (
          <FactCell
            plot={row.original}
            factType={R1FactType.r1_soil_moisture_latest_analysis_per_ha}
            unitType={UnitEnum['m^3/ha']}
            icon={WaterIcon}
          />
        );
      },
    },
    {
      id: 'precipitationChange',
      accessorKey: 'precipitationChange',
      header: ({ column }) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.water.labels.precipitationChange')}
        </ToggleSortButton>
      ),
      cell: ({ row }) => {
        return (
          <FactTrendCell
            plot={row.original}
            factType={R1FactType.r1_precipitation_trend_change}
            unitType={UnitEnum['%']}
          />
        );
      },
    },
  ];

  const widgetSlotsConfiguration = useWidgetSlotConfigurationByNameType(
    DefaultWidgetNames.default_template_project_water,
    WidgetType.t_project_indicator_page,
  );

  return (
    <LandMonitoringDetailPageWrapper title={t('shared.ncaDetail.details.water.labels.water')}>
      <Stack spacing={8}>
        <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
        <SuspenseBoundary
          fallback={
            <Stack center className='h-[600px]'>
              <Loader />
            </Stack>
          }
        >
          <div>
            <PlotListTitle
              title={t('shared.ncaDetail.details.water.labels.waterDataPerPlot')}
              action={!isSmallScreen ? <WaterExplainer /> : null}
            />
            <PlotListDesktop customColumns={customPlotListColumns} />
          </div>
        </SuspenseBoundary>
      </Stack>
    </LandMonitoringDetailPageWrapper>
  );
};
